import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { useCustomers } from "@/hooks/use-api";
import { ListPage } from "@/components/v2/list-page";
import { DataTable } from "@/components/v2/data-table";
import { EmptyState } from "@/components/v2/empty-state";
import { ViewSwitcher } from "@/components/v2/view-switcher";
import { AggregateStrip } from "@/components/v2/aggregate-strip";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatCurrency } from "@/lib/utils";
import { LIST_PAGE_VIEWS } from "@/pages/v2/_shared/list-page-views";
import { formatShowingRange } from "@/pages/v2/_shared/format-showing-range";
import { useAddDemoAccountStore } from "@/stores/add-demo-account-store";

type Row = Record<string, unknown>;

const columns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "name",
    header: "Name",
    cell: ({ row }) => {
      const first = row.original.first_name as string | undefined;
      const last = row.original.last_name as string | undefined;
      const fullName = [first, last].filter(Boolean).join(" ") || "—";
      const company = row.original.company_name as string | undefined;
      return (
        <div>
          <p className="font-medium transition-colors group-hover:text-[color:var(--color-accent)]">
            {fullName}
          </p>
          {company && (
            <p className="typo-label text-[color:var(--color-text-lo)] normal-case tracking-normal">
              {company}
            </p>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "email",
    header: "Email",
    cell: ({ row }) => (
      <span className="text-[color:var(--color-text-md)]">
        {(row.original.email as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "phone",
    header: "Phone",
    enableSorting: false,
    cell: ({ row }) => (
      <span className="text-[color:var(--color-text-md)]">
        {(row.original.phone as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "assigned_rep_name",
    header: "Rep",
    cell: ({ row }) => {
      const rep = row.original.assigned_rep_name as string | undefined;
      return (
        <span className={rep ? "" : "text-[color:var(--color-text-lo)]"}>
          {rep || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "lifecycle_status",
    header: "Status",
    cell: ({ row }) => (
      <StatusBadge status={row.original.lifecycle_status as string} />
    ),
  },
  {
    accessorKey: "opportunity_count",
    header: "Opportunities",
    meta: { numeric: true },
    cell: ({ row }) => (
      <span className="text-[color:var(--color-text-md)]">
        {(row.original.opportunity_count as number) ?? 0}
      </span>
    ),
  },
  {
    accessorKey: "total_revenue",
    header: "Revenue",
    meta: { numeric: true },
    cell: ({ row }) => (
      <span className="font-medium">
        {formatCurrency(row.original.total_revenue as number)}
      </span>
    ),
  },
];

const VIEWS = LIST_PAGE_VIEWS.customers;

export default function V2CustomersPage() {
  const navigate = useNavigate();
  const { openDialog } = useAddDemoAccountStore();
  const [view, setView] = useState<string>(VIEWS.defaultValue);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "created_at", desc: true },
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const pageSize = 50;

  // Debounce raw keystrokes into the query we send to the server (mirrors
  // settings/members.tsx). 300ms matches that precedent.
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // A new query almost always has fewer pages — reset to page 1 so we never
  // land on an out-of-range (empty) page.
  useEffect(() => {
    setPageIndex(0);
  }, [searchQuery]);

  const { data, isLoading, isError, refetch } = useCustomers({
    offset: pageIndex * pageSize,
    limit: pageSize,
    // Server-side search (see .claude/rules/database-calculations.md): the
    // client holds only one page, so filtering must happen on the server.
    // Empty → buildQuery drops the param → full list.
    search: searchQuery || undefined,
    sort_by: sorting[0]?.id,
    sort_order: sorting[0]?.desc ? "desc" : "asc",
  });

  const customers = (data?.customers ?? []) as Row[];
  const total = data?.total ?? 0;

  const isSearching = searchQuery.length > 0;
  const emptyMessage = isSearching
    ? "No customers match your search"
    : "No customers yet";
  const emptyDescription = isSearching
    ? `No customers match "${searchQuery}". Try a different name or email.`
    : "Customers live here — the people and organizations you sell to. Add your first or import from a spreadsheet.";

  // TODO(ADV-787-followup): Total LTV / Avg LTV / Open opps cells need a
  // DB-side aggregate view. Render `—` until that lands.
  const aggregateItems = [
    {
      label: "Showing",
      value: formatShowingRange(customers.length, pageIndex, pageSize, total),
    },
    { label: "Total LTV", value: "—" },
    { label: "Avg LTV", value: "—" },
    { label: "Open opps", value: "—" },
  ];

  return (
    <ListPage
      title="Customers"
      actions={
        <Button onClick={() => navigate("/customers/new")}>Add customer</Button>
      }
      tabs={
        <ViewSwitcher
          value={view}
          onValueChange={setView}
          options={[...VIEWS.options]}
          aria-label="Customers view"
        />
      }
      filters={
        <div className="relative w-72">
          <Search
            className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--color-text-md)]"
            aria-hidden="true"
          />
          <Input
            type="search"
            aria-label="Search customers"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name or email…"
            className="pl-8"
          />
        </div>
      }
      aggregateStrip={
        view === "table" ? (
          <AggregateStrip
            items={aggregateItems}
            sort={{ label: "Created", direction: "desc" }}
          />
        ) : undefined
      }
    >
      {view === "table" && (
        <DataTable
          columns={columns}
          data={customers}
          total={total}
          isLoading={isLoading}
          isError={isError}
          errorEntityLabel="customers"
          onRetry={() => refetch()}
          onRowClick={(row) => {
            const contactId = row.contact_id as string | undefined | null;
            const id = row.id as string | undefined | null;
            const target = contactId && contactId.length > 0 ? contactId : id;
            if (target) navigate(`/customers/${target}`);
          }}
          emptyMessage={emptyMessage}
          emptyDescription={emptyDescription}
          pageSize={pageSize}
          pageIndex={pageIndex}
          onPaginationChange={setPageIndex}
          sorting={sorting}
          onSortingChange={setSorting}
        />
      )}
      {view === "analytics" && (
        <EmptyState
          title="Analytics ships in Wave 2"
          description="The Customers analytics surface is part of the broader operational-screens plan."
          secondaryAction={{
            label: "Try Adva with sample data",
            onClick: openDialog,
          }}
        />
      )}
    </ListPage>
  );
}
